import { dotFormatterStrategy } from './components/header/utils/format-value.js';
import {
  emptyHeaderFormatter,
  formatValueRange,
} from './components/header/utils/value-formatters.js';
import { OVERLAY_TOOLTIP_MESSAGES } from './messages.js';
import type {
  DefaultConfig,
  DefaultConfigByGeometry,
  DefaultConfigByGeoLayer,
} from './types.js';

type ActionsConfig = DefaultConfig['defaultActionsConfig'];

const BASE_CONFIG = {
  defaultActionsConfig: new Map([
    [
      'name',
      {
        field: 'seriesLabel',
      },
    ],
    [
      'value',
      {
        field: 'valueLabel',
      },
    ],
  ]) as ActionsConfig,
};

const BASE_COORDINATES_CONFIG = {
  defaultActionsConfig: new Map([
    [
      'name',
      {
        field: 'valueLabel',
      },
    ],
  ]) as ActionsConfig,
};

const BASE_XY_CONFIG = {
  ...BASE_CONFIG,
  headerFormatStrategy: dotFormatterStrategy,
  headerLabel: OVERLAY_TOOLTIP_MESSAGES.xValue,
  seriesLabel: OVERLAY_TOOLTIP_MESSAGES.series,
  valueLabel: OVERLAY_TOOLTIP_MESSAGES.yValue,
  sharedVariant: true,
};

/** @internal */
export const GEO_DEFAULTS: DefaultConfigByGeoLayer = {
  bubble: {
    ...BASE_COORDINATES_CONFIG,
    seriesLabel: OVERLAY_TOOLTIP_MESSAGES.location,
    valueLabel: OVERLAY_TOOLTIP_MESSAGES.coordinates,
    sharedVariant: true,
  },
  choropleth: {
    defaultActionsConfig: new Map([
      [
        'name',
        {
          field: 'seriesLabel',
        },
      ],
    ]) as ActionsConfig,
    seriesLabel: OVERLAY_TOOLTIP_MESSAGES.location,
    valueLabel: OVERLAY_TOOLTIP_MESSAGES.value,
  },
  connection: {
    ...BASE_COORDINATES_CONFIG,
    seriesLabel: OVERLAY_TOOLTIP_MESSAGES.location,
    valueLabel: OVERLAY_TOOLTIP_MESSAGES.coordinates,
    sharedVariant: true,
  },
  dot: {
    ...BASE_COORDINATES_CONFIG,
    seriesLabel: OVERLAY_TOOLTIP_MESSAGES.location,
    valueLabel: OVERLAY_TOOLTIP_MESSAGES.coordinates,
  },
};

export const DEFAULTS: DefaultConfigByGeometry = {
  dot: { ...BASE_XY_CONFIG, sharedVariant: false },
  line: { ...BASE_XY_CONFIG },
  area: { ...BASE_XY_CONFIG },
  bar: { ...BASE_XY_CONFIG },
  rect: {
    ...BASE_CONFIG,
    symbolPosition: 'right',
    headerFormatStrategy: formatValueRange,
    headerLabel: OVERLAY_TOOLTIP_MESSAGES.xValue,
    seriesLabel: OVERLAY_TOOLTIP_MESSAGES.yValue,
    valueLabel: OVERLAY_TOOLTIP_MESSAGES.value,
    headerShowRangeDuration: true,
  },
  pie: {
    ...BASE_CONFIG,
    headerFormatStrategy: emptyHeaderFormatter,
    seriesLabel: OVERLAY_TOOLTIP_MESSAGES.category,
    valueLabel: OVERLAY_TOOLTIP_MESSAGES.value,
  },
  meterbar: {
    ...BASE_CONFIG,
    seriesLabel: OVERLAY_TOOLTIP_MESSAGES.segment,
    valueLabel: OVERLAY_TOOLTIP_MESSAGES.value,
  },
  multimeterbar: {
    ...BASE_CONFIG,
    seriesLabel: OVERLAY_TOOLTIP_MESSAGES.segment,
    valueLabel: OVERLAY_TOOLTIP_MESSAGES.value,
    sharedVariant: true,
  },
  treemap: {
    ...BASE_CONFIG,
    seriesLabel: OVERLAY_TOOLTIP_MESSAGES.node,
    valueLabel: OVERLAY_TOOLTIP_MESSAGES.value,
  },
  honeycomb: {
    ...BASE_CONFIG,
    headerFormatStrategy: emptyHeaderFormatter,
    symbolPosition: 'right',
    seriesLabel: OVERLAY_TOOLTIP_MESSAGES.node,
    valueLabel: OVERLAY_TOOLTIP_MESSAGES.value,
  },
  geoBubble: GEO_DEFAULTS.bubble,
  geoChoropleth: GEO_DEFAULTS.choropleth,
  geoConnection: GEO_DEFAULTS.connection,
  geoDot: GEO_DEFAULTS.dot,
};
